import { PhoenixSubscription, PhoenixSubscriptionSettings } from 'phoenix/domain';

export type ISubscriptionsService = SubscriptionsService;

/**
* @ngdoc service
* @name  sharebase.subscriptions.sbSubscriptionsService
* 
* @description 
* A service used to retrieve and interact with a ShareBase user's subscriptions
**/
export class SubscriptionsService {
	public static readonly $inject = [
		'$http',
		'$q',
		'settingsService',
		'pxUriPathService'
	];

	constructor(
		private readonly $http: ng.IHttpService,
		private readonly $q: ng.IQService,
		private readonly settingsService,
		private readonly pxUriPathService
	) { }


	/**
	* @ngdoc method
	* @name sbSubscriptionsService#getFolderSettings
	* @methodOf sharebase.subscriptions.sbSubscriptionsService
	* @kind function
	*
	* @description
	* Gets a user's notification settings for a folder
	*
	* @param {object} folderId A folder id to get settings
	*
	* @returns {Deferred} A promise that will resolve with a user's 
	* notification settings for a folder
	* 
	*/
	public getFolderSettings(folderId): ng.IPromise<PhoenixSubscription> {
		if(!folderId || typeof folderId !== 'number'){
			return this.$q.reject('Invalid folderId supplied!');
		}

		return this.$http
			.get(this.settingsService.serverAPIPath + '/f/' + folderId + '/notification-settings')
			.then(function(response){
				return response.data as PhoenixSubscription;
			});
	}

	/**
	* @ngdoc method
	* @name sbSubscriptionsService#getDocumentSettings
	* @methodOf sharebase.subscriptions.sbSubscriptionsService
	* @kind function
	*
	* @description
	* Gets a user's notification settings for a document
	*
	* @param {object} documentId A document id to get settings
	*
	* @returns {Deferred} A promise that will resolve with a user's 
	* notification settings for a document
	* 
	*/
	public getDocumentSettings(documentId): ng.IPromise<PhoenixSubscription> {
		if(!documentId || typeof documentId !== 'number'){
			return this.$q.reject('Invalid documentId supplied!');
		}

		return this.$http
			.get(this.settingsService.serverAPIPath + '/documents/' + documentId + '/notification-settings')
			.then(function(response){
				return response.data as PhoenixSubscription;
			});
	}


	/**
	* @ngdoc method
	* @name sbSubscriptionsService#updatedItemSettings
	* @methodOf sharebase.subscriptions.sbSubscriptionsService
	* @kind function
	*
	* @description
	* Updates a user's notification settings for an object
	*
	* @param {object} settingObj An items setting object
	*
	* @returns {Deferred} A promise that will resolve with status of update
	* 
	*/		
	public updateItemSettings(settingsObj: PhoenixSubscription) {			
		if(!angular.isObject(settingsObj)){
			return this.$q.reject('Invalid settings object.');
		}
		if(!settingsObj.Links.SettingsUri){
			return this.$q.reject('Invalid settings object.');
		}

		return this.$http.put(settingsObj.Links.SettingsUri, settingsObj.Settings);
	}

	/**
 	* @ngdoc method
	* @name sbSubscriptionsService#getDigestSettings
	* @methodOf sharebase.subscriptions.sbSubscriptionsService
	* @kind function
	*
	* @description
	* Get the email digest settings for the current user
	*
	* @returns {Deferred} A promise that will resolve with the user's email digest settings
	*/
	public getNotificationSettings(): ng.IPromise<PhoenixSubscriptionSettings> {
		return this.$http.get(this.pxUriPathService.resolvePath('/user/notification-settings/preferences'))
			.then(res => res.data as PhoenixSubscriptionSettings);
	}

	/**
 	* @ngdoc method
	* @name sbSubscriptionsService#saveDigestSettings
	* @methodOf sharebase.subscriptions.sbSubscriptionsService
	* @kind function
	*
	* @description
	* Save the user's email digest settings
	*
	* @param settings The settings to save
	* @returns {Deferred} An angular http promise.
	*/
	public saveNotificationSettings(settings: PhoenixSubscriptionSettings): ng.IPromise<any> {
		return this.$http.put(this.pxUriPathService.resolvePath('/user/notification-settings/preferences'), settings);
	}
}


// WEBPACK FOOTER //
// ./src/sharebase/services/subscriptionsService.ts